import React, { Fragment, useEffect, useState } from 'react'
import { Card, CardDetailLoading } from '../../../common'
import { OfferDetails } from '../../../services/my-coupon-bff/types'
import { DATE_FORMAT, getDate } from '../../../utils/date'
import { getStatus, PREMIUM_STATUS } from '../../../utils/status'
import { ErrorResponse } from '../../../utils/reponseType'
import Loading from '../../premium/Loading'
import classNames from 'classnames'
import EmptyOffersSpecialLotus from './EmptyOffersSpecialLotus'
import { LOTUS } from '../../../utils/constant'
import { addGAEvent } from '../../../utils/addGoogleAnalytic'

export interface Props {
  loading: boolean
  setLoading: (loading: boolean) => void
  name: string
  point: number | undefined
  offerId?: string
  setLockScroll: (lockScroll: boolean) => void
  selectedType: string
  isHideSubMenu: boolean
  onRefresh: Function
  offers: OfferDetails[] | null
  loadingAvailable: boolean
  error: ErrorResponse | null
  onSelectOffer: (offerDetails: OfferDetails) => void
}

const OffersSpecialLotusList: React.FC<Props> = ({
  loading,
  point,
  offerId,
  setLockScroll,
  error,
  selectedType,
  offers,
  loadingAvailable,
  onSelectOffer
}) => {
  useEffect(() => {
    if (window.parent) {
      window.parent.postMessage('iframe:ready', '*')
    }
  }, [])

  const sortedOffers = (offers: OfferDetails[] | null | undefined) => {
    if (offers && Array.isArray(offers)) {
      const emptyOffers: OfferDetails[] = []
      const notEnoughOffers: OfferDetails[] = []
      const remainderOffers: OfferDetails[] = []
      const validOffers: OfferDetails[] = []

      offers.forEach(offer => {
        const { 
          price, 
          availableInventoryRemaining, 
          offerDetails: { 
            acquisitionStartDate:  startDate,
            acquisitionEndDate: endDate
          },
        } = offer
        const status = getStatus({ startDate, endDate, point, price, available: availableInventoryRemaining })
        if (status === PREMIUM_STATUS.EMPTY) {
          emptyOffers.push(offer)
        } else if (status === PREMIUM_STATUS.NOT_ENOUGH) {
          notEnoughOffers.push(offer)
        }  else if (status === PREMIUM_STATUS.COMING_SOON) {
          remainderOffers.push(offer)
        } else {
          validOffers.push(offer)
        }
      })

      return [...validOffers, ...notEnoughOffers, ...remainderOffers, ...emptyOffers]
    }
    return []
  }

  const handleOpenOfferDetail = (id: string) => {
    setLockScroll(true)
    const offerDetail = sortedOffers(offers as OfferDetails[]).find(offer => offer.id === id)
    onSelectOffer(offerDetail!)
    const internalName = offerDetail?.offerDetails.internalName || 'unknown_offer'
    addGAEvent({
      event: 'select_offer_item',
      offer_name: internalName,
    })
  }

  if (loading) {
    return <>
      {offerId ? <CardDetailLoading idElement="offers-special-lotus-card-loading" /> : <div id="offers-special-lotus-loading-container" data-testid='loading-offers-special-lotus'><Loading idElement="offers-special-lotus-loading" /></div>}
    </>
  }

  if (error) {
    if (error?.code === 401) {
      return <>{offerId ? <CardDetailLoading idElement="offers-special-lotus-card-loading" /> : <Loading idElement="offers-special-lotus-loading" />}</>
    } else {
      return <Fragment />
    }
  }

  if (!offers?.length && !loading) {
    const isEmptyOfferFromSubType = !!selectedType && offers?.length === 0

    return (
      <EmptyOffersSpecialLotus
        isHasHeader={isEmptyOfferFromSubType}
        message={
          isEmptyOfferFromSubType ? 'ติดตามรีวอร์ดได้เร็วๆนี้' : 'ติดตามรีวอร์ดได้เร็วๆนี้'
        }
      />
    )
  }

  return (
    <div
      id="offers-special-lotus-list"
      data-testid='offers-special-lotus-list-component'
      className={classNames(
        `flex flex-col`,
        {
          'hidden': !offers?.length
        },
      )}
    >
      {offers?.length &&
        offers.map(({ id, price, offerDetails, redemptionEndDate, redemptionStartDate }, index) => {
          const date = getDate(redemptionEndDate!, DATE_FORMAT)
          let status = null

          if (!loadingAvailable) {
            const availableInventoryRemaining = offers[index]?.availableInventoryRemaining
            status = getStatus({ startDate: redemptionStartDate!, endDate: redemptionEndDate!, point, price, available: availableInventoryRemaining })
          }

        return (
          <Fragment key={id}>
            <div
              id={`offers-special-lotus-list-content${index}`}
              data-testid={`card-component-${id}`}
              onClick={() => handleOpenOfferDetail(id)}
              className={classNames(
                {
                  'pb-[16px]': index === offers.length - 1
                }
              )}
            >
              <Card
                idElement={`offers-special-lotus-list-card${index}`}
                key={id}
                company={LOTUS}
                title={offerDetails.title}
                fontTitle='text-[16px] leading-6'
                date={date}
                status={status}
                image={offerDetails.media.url}
                loading={loadingAvailable && point !== null}
                startDate={getDate(redemptionStartDate!, 'DD MMM BBBB, HH:mm')}
                rewardType={offerDetails.offerType?.match(/GOLD/i) ? 'GOLD' : ''}
              />
            </div>
          </Fragment>
        )
      })}
    </div>
  )
}

export default OffersSpecialLotusList
